import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PersonalityNumberCalculator = () => {
  const [fullName, setFullName] = useState('');
  const [personalityNumber, setPersonalityNumber] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setFullName(e.target.value);
    setError('');
  };

  const calculatePersonalityNumber = () => {
    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    // Extract consonants and convert to uppercase
    const consonants = fullName.match(/[BCDFGHJKLMNPQRSTVWXYZ]/gi) || [];
    const name = consonants.join('').toUpperCase();

    // Letter to number mapping
    const letterValues = {
      B: 2, C: 3, D: 4, F: 6, G: 7,
      H: 8, J: 1, K: 2, L: 3, M: 4,
      N: 5, P: 7, Q: 8, R: 9, S: 1,
      T: 2, V: 4, W: 5, X: 6, Y: 7, Z: 8
    };

    // Calculate consonant number
    const consonantNumber = name.split('').reduce((sum, letter) => {
      return sum + (letterValues[letter] || 0);
    }, 0);

    // Reduce to single digit or master number
    const reduceNumber = (num) => {
      while (num > 9 && ![11, 22, 33].includes(num)) {
        num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
      }
      return num;
    };

    const finalNumber = reduceNumber(consonantNumber);

    setPersonalityNumber(finalNumber);

    // Generate chart data
    setChartData({
      labels: name.split('').concat(['Total', 'Personality Number']),
      datasets: [{
        label: 'Consonant Values',
        data: name.split('').map(letter => letterValues[letter] || 0).concat([consonantNumber, finalNumber]),
        backgroundColor: [
          ...name.split('').map(() => 'rgba(75, 192, 192, 0.5)'),
          'rgba(255, 159, 64, 0.5)',
          'rgba(153, 102, 255, 0.5)'
        ],
        borderColor: [
          ...name.split('').map(() => 'rgba(75, 192, 192, 1)'),
          'rgba(255, 159, 64, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const personalityMeanings = {
    1: 'Leadership and independence',
    2: 'Harmony and cooperation',
    3: 'Creativity and self-expression',
    4: 'Stability and practicality',
    5: 'Freedom and adventure',
    6: 'Responsibility and nurturing',
    7: 'Spirituality and introspection',
    8: 'Ambition and power',
    9: 'Humanitarianism and compassion',
    11: 'Inspiration and intuition',
    22: 'Master builder and vision',
    33: 'Master teacher and compassion'
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Personality Number Calculator | Discover Your Outer Personality</title>
        <meta name="description" content="Calculate your Personality Number using our Numerology Calculator. Discover how others perceive you." />
        <meta name="keywords" content="personality number, numerology calculator, name numerology, outer personality" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/personality-number" />
        <meta property="og:title" content="Personality Number Calculator | Discover Your Outer Personality" />
        <meta property="og:description" content="Calculate your Personality Number using our Numerology Calculator. Discover how others perceive you." />
        <meta property="og:image" content="https://yourwebsite.com/images/personality-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/personality-number" />
        <meta property="twitter:title" content="Personality Number Calculator | Discover Your Outer Personality" />
        <meta property="twitter:description" content="Calculate your Personality Number using our Numerology Calculator. Discover how others perceive you." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/personality-og-image.jpg" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🎭</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Personality Number Calculator</h1>
            <p className="text-lg text-teal-600 font-semibold">Discover Your Outer Personality</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fullName">
            Full Name:
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={fullName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your full name"
          />
        </div>

        <button
          onClick={calculatePersonalityNumber}
          className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Personality Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {personalityNumber && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Personality Number</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-teal-600 mb-4">
                {personalityNumber}
              </div>
              <p className="text-gray-700">
                This number reveals how others perceive you and your outer personality.
              </p>
            </div>
            {chartData && (
              <div className="mt-8 space-y-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Calculation Breakdown</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Personality Number Calculation Steps'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Example Calculation</h4>
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      For the name "John Doe":
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Consonants:</p>
                        <p>J, H, N, D</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Values:</p>
                        <p>1 (J) + 8 (H) + 5 (N) + 4 (D) = 18</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Result:</p>
                        <p>1 + 8 = 9</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Consonant Values</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">B</p>
                      <p>2</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">C</p>
                      <p>3</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">D</p>
                      <p>4</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">F</p>
                      <p>6</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">G</p>
                      <p>7</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Your Personality Meaning</h4>
                  <p className="text-gray-700">
                    {personalityMeanings[personalityNumber]}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Personality Number:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-teal-600 mb-4">Step 1: Enter Full Name</h3>
              <p className="text-gray-700">
                Input your complete full name (first, middle, last).
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-teal-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Personality Number.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-teal-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Personality Number and its meaning in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is a Personality Number?</h3>
              <p className="text-gray-700">
                Your Personality Number reveals how others perceive you and your outward personality traits.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                It's calculated by converting the consonants in your name to their corresponding numbers, summing them, and reducing to a single digit or master number.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What's the difference between Personality and Soul Urge numbers?</h3>
              <p className="text-gray-700">
                The Personality Number represents your outer self, while the Soul Urge Number represents your inner desires and motivations.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-teal-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Personality Number calculation</li>
                <li>Visual breakdown of calculation steps</li>
                <li>Supports master numbers (11, 22, 33)</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-teal-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand how others perceive you</li>
                <li>Discover your outer personality traits</li>
                <li>Improve your social interactions</li>
                <li>Align your outer self with your inner self</li>
                <li>Make better first impressions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalityNumberCalculator;
